import { createApi } from "@reduxjs/toolkit/query/react";
import { defaultBaseQuery } from "../../utils/API/fetchBaseQuery";
import { transformRequest } from "../../utils/API/requestMiddleware";
import { transformErrorResponse, transformSuccessResponse } from "../../utils/API/responseMiddleware";

const clientValuationAPI = createApi({
    baseQuery: defaultBaseQuery,
    reducerPath: 'clientAPI',
    endpoints: (builder) => ({

        addClientValuation: builder.mutation({
            query: (data) => ({
                url: `/ClientValuation/AddClientValuation`,
                method: `POST`,
                body: transformRequest(data)
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        updateClientValuation: builder.mutation({
            query: (data) => ({
                url: `/ClientValuation/UpdateClientValuation`,
                method: `POST`,
                body: transformRequest(data)
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        getClientValuationListById: builder.mutation({
            query: (planID) => ({
                url: `/ClientValuation/GetClientValuationById?PlanID=${planID}`,
                method: `POST`,
                body: transformRequest(planID)
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        getClientCopyValuation: builder.mutation({
            query: (planID) => ({
                url: `ClientValuation/GetClientCopyValuation?PlanID=${planID}`,
                method: `POST`,
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        getClientValuationByValId: builder.query({
            query: (valID) => ({
                url: `/ClientValuation/GetClientValuationByValId?ValID=${valID}`,
                method: `GET`,
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        deleteClientValuation: builder.mutation({
            query: (valID) => ({
                url: `/ClientValuation/DeleteClientValuation?ValID=${valID}`,
                method: `DELETE`,
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        getAllClientValuationList: builder.query({
            query: () => ({
                url: `/ClientValuation/GetAllClientValuation`,
                method: `GET`,
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse
        }),

        addSet: builder.mutation({
            query: (data) => ({
                url: `/ClientValuation/AddSet`,
                method: `POST`,
                body: transformRequest(data)
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        deleteSet: builder.mutation({
            query: (reportTemplateID) => ({
                url: `/ClientValuation/DeleteSet?ReportTemplateID=${reportTemplateID}`,
                method: `DELETE`,
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        performCalculation: builder.mutation({
            query: (data) => ({
                url: `/ClientValuation/PerformCalculation`,
                method: `POST`,
                body: transformRequest(data)
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        createWordMerge: builder.query({
            query: (data) => ({
                url: `/ClientValuation/WordMerge?ValID=${data.valID}&WordDocPathAndName=${data.wordDocPathAndName}&outFileName=${data.outFileName}&WordTemplId=${data.WordTemplId}`,
                method: `GET`,
                responseHandler: (response) => response.blob(),
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse
        }),
        priorFromCopyFields: builder.mutation({
            query: (data) => ({
                url: `/ClientValuation/PriorFromCopyFields`,
                method: `POST`,
                body: transformRequest(data)
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        GetCsvSegment: builder.query({
            query: (id) => ({
                url: `/ClientValuation/GetCsvSegment?segment=${id}`,
                method: 'GET',
            }),
        }),

        updateXMLReportFieldsForMapping: builder.mutation({
            query: (data) => ({
                url: `/ClientValuation/UpdateXMLReportFieldsForMapping`,
                method: `POST`,
                body: transformRequest(data)
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        getManageXmlReport: builder.query({
            query: (data) => ({
                url: `/ClientValuation/GetManageXMLReport?XmlReportId=${data.XmlReportId}&ValId=${data.valId}&TemplateId=${data.templateId}`,
                method: 'GET',
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }), 
        
        importReportFields: builder.mutation({
            query: (data) => ({
                url: `/ClientValuation/ImportReportField`,
                method: `POST`,
                body: transformRequest(data)
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        getClientValuationByReportWordTempId: builder.mutation({
            query: (reportTemplateID) => ({
                url: `/ClientValuation/GetClientValuationByReportWordTempId?ReportTemplateID=${reportTemplateID}`,
                method: `POST`,
                body: transformRequest(reportTemplateID)
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        getClientValuationByReportXmlTempId: builder.mutation({
            query: (reportTemplateID) => ({
                url: `/ClientValuation/GetClientValuationByReportXmlTempId?ReportTemplateID=${reportTemplateID}`,
                method: `POST`,
                body: transformRequest(reportTemplateID)
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),
         exportCsv: builder.query({
            query: (data) => ({
                url: `/ClientValuation/ExportCsv?ValID=${data.valID}&WordDocPathAndName=${data.wordDocPathAndName}&outFileName=${data.outFileName}`,
                method: `GET`,
                responseHandler: (response) => response.blob(),
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse
        }),

    })
});

export const {
    useAddClientValuationMutation,
    useUpdateClientValuationMutation,
    useGetClientValuationListByIdMutation,
    useGetClientCopyValuationMutation,
    useDeleteClientValuationMutation,
    useLazyGetAllClientValuationListQuery,
    useLazyGetClientValuationByValIdQuery,
    useAddSetMutation,
    useDeleteSetMutation,
    usePerformCalculationMutation,
    useLazyCreateWordMergeQuery,
    useUpdateXMLReportFieldsForMappingMutation,
    usePriorFromCopyFieldsMutation,
    useLazyGetCsvSegmentQuery,
    useLazyGetManageXmlReportQuery,
    useImportReportFieldsMutation,
    useGetClientValuationByReportWordTempIdMutation,
    useGetClientValuationByReportXmlTempIdMutation,
    useLazyExportCsvQuery
} = clientValuationAPI;

export default clientValuationAPI;